import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  TrashIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline'
import { ArrowRightToLine, ClipboardCheckIcon } from 'lucide-react'

import { Loading } from '@/components'
import { postMediaUrl } from '@/api/mediaAPI'
import { getErrorMessage } from '@/utils/errorHandlers'
import {
  deleteContainerBatch,
  deleteContainerDocuments,
  deleteContainerCosts,
  postContainerCosts,
  patchContainerCosts,
  postContainerDocuments,
  patchContainerDocuments,
  postContainerBatches,
  patchContainerBatch,
  updateContainerDetails,
  updateContainerAdvancedDetails,
  /*   updateContainerLogistics, */
  deleteContainer,
  ungroupContainer,
} from '@/api/containersAPI'
import { postProducts } from '@/api/productsAPI'
import { useContainerFullDetails } from '../hooks/useContainerFullDetails'
import {
  useCountriesFetcher,
  useCategoriesFetcher,
  useProviderCompaniesFetcher,
  useManagerCompaniesFetcher,
  useOriginsFetcher,
  useDestinationsFetcher,
} from '../hooks/useContainerQueries'
import { formatDateSimple } from '@/utils/helpers'
import { EditDetailsDialog } from '../components/dialogs/EditDetailsDialog'
import { EditAdvancedDetailsDialog } from '../components/dialogs/EditAdvancedDetailsDialog'
import { InsuranceDialog } from '../dialogs/InsuranceDialog'
import { CONTAINER_STATUS_LABELS } from '../filters/constants/statusOptions'

import { translateCostType } from '../utils/translators'
import type { BatchRow, DocumentRow, CostRow } from '../interfaces/tableTypes'

import {
  EditableCard,
  DetailRow,
  ActionButton,
  ConfirmDialog,
  PageWrapper,
  DetailPageLayout,
  ContentCard,
  GenericTable,
  StatusBadgeWithIcon,
  type TableColumn,
} from '@/components/newRedesign'
import {
  CostDialog,
  DocumentDialog,
  BatchDialog,
  EditDocumentDialog,
} from '@/containers/logistic/shared/dialogs'
import { ProductDialog } from '../dialogs/ProductDialog'
import type { ProductFormData } from '../dialogs/ProductDialog'
import type {
  CostFormData,
  BatchFormData,
  EditDocumentFormData,
} from '@/containers/logistic/shared/dialogs'
import { DispatchDialog } from '../components/dialogs/DispatchDialog'

export default function ContainerDetailsPage() {
  const { containerId } = useParams<{ containerId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const containerIdNum = parseInt(containerId || '0', 10)

  const { data, isLoading, refetch } = useContainerFullDetails(containerIdNum)

  // Usar fetchers con cache de React Query
  const fetchCountries = useCountriesFetcher()
  const fetchCategories = useCategoriesFetcher()
  const fetchCompanies = useProviderCompaniesFetcher()
  const fetchManagerCompanies = useManagerCompaniesFetcher()
  const fetchOrigins = useOriginsFetcher()
  const fetchDestinations = useDestinationsFetcher()

  // Mutations
  const updateDetailsMutation = useMutation({
    mutationFn: (data: {
      status?: string
      name?: string
      operationNumber?: string
      countryFromId?: number
      categories?: number[]
      isReturned?: boolean
      ownerCompanyId?: number | null
      managerCompanyId?: number | null
      observations?: string
    }) => updateContainerDetails({ id: containerIdNum, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['container', containerIdNum] })
      queryClient.invalidateQueries({ queryKey: ['containers'] })
    },
  })

  const updateAdvancedDetailsMutation = useMutation({
    mutationFn: (data: {
      originId?: number
      destinyId?: number
      netWeight?: number
      grossWeight?: number
      code?: string
      sealNumber?: string
      isRefrigered?: boolean
      temperatureRange?: string
      trackingNumber?: string
    }) => updateContainerAdvancedDetails({ id: containerIdNum, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['container', containerIdNum] })
      queryClient.invalidateQueries({ queryKey: ['containers'] })
    },
  })

  /* const _updateLogisticsMutation = useMutation({
    mutationFn: (data: {
      storageCompanyId?: number | null
      transporterCompanyId?: number | null
      transportedById?: number | null
      vehicleId?: number | null
    }) => updateContainerLogistics({ id: containerIdNum, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['container', containerIdNum] })
      queryClient.invalidateQueries({ queryKey: ['containers'] })
    },
  })
 */
  const deleteContainerMutation = useMutation({
    mutationFn: () => deleteContainer(containerIdNum),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers'] })
    },
  })

  const dispatchContainerMutation = useMutation({
    mutationFn: (data: {
      containerId: string
      areaId: number
      // observations?: string // Not used in ungroup endpoint
    }) => ungroupContainer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers'] })
      queryClient.invalidateQueries({ queryKey: ['dispatches'] })
      queryClient.invalidateQueries({ queryKey: ['containers-products'] })
      queryClient.invalidateQueries({ queryKey: ['product-details'] })
      toast.success('Contenedor desagrupado correctamente')
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage)
    },
  })

  const createCostMutation = useMutation({
    mutationFn: (params: {
      type: string
      registeredPrice: { amount: number; codeCurrency: string }
      fixedCostCategoryId: number
      observations?: string
    }) => postContainerCosts({ id: containerIdNum, params }),
    onSuccess: () => {
      toast.success('Costo creado correctamente')
      setShowCostDialog(false)
      setEditingCost(null)
      refetch()
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al crear el costo')
    },
  })

  const updateCostMutation = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number
      params: {
        type: string
        registeredPrice: { amount: number; codeCurrency: string }
        fixedCostCategoryId: number
        observations?: string
      }
    }) => patchContainerCosts({ id, params }),
    onSuccess: () => {
      toast.success('Costo actualizado correctamente')
      setShowCostDialog(false)
      setEditingCost(null)
      setCostMode('create')
      refetch()
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al actualizar el costo')
    },
  })

  const createDocumentMutation = useMutation({
    mutationFn: (params: {
      name: string
      path: string
      observations?: string
    }) => postContainerDocuments({ id: containerIdNum, params }),
    onSuccess: () => {
      // No mostrar toast aquí, lo manejamos en el DocumentDialog
      refetch()
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al adjuntar el documento')
    },
  })

  const updateDocumentMutation = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number
      params: {
        name: string
        observations?: string
      }
    }) => patchContainerDocuments({ id, params }),
    onSuccess: () => {
      toast.success('Documento actualizado correctamente')
      setShowEditDocumentDialog(false)
      setEditingDocument(null)
      refetch()
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al actualizar el documento')
    },
  })

  const createBatchMutation = useMutation({
    mutationFn: (params: BatchFormData) =>
      postContainerBatches({
        id: containerIdNum,
        batches: [
          {
            productId: Number(params.productId),
            expirationAt: params.expirationAt as string,
            entryQuantity: Number(params.entryQuantity),
            noPackages: params.noPackages
              ? Number(params.noPackages)
              : undefined,
            supplierId: params.supplierId
              ? Number(params.supplierId)
              : undefined,
            registeredPrice: {
              amount: Number(params.registeredPrice.amount),
              codeCurrency: params.registeredPrice.codeCurrency,
            },
            description: params.description || undefined,
          },
        ],
      }),
    onSuccess: (_, variables) => {
      if (!variables.productId || showBatchDialog) {
        toast.success('Lote creado correctamente')
      }
      setShowBatchDialog(false)
      setEditingBatch(null)
      setEditingBatchFull(null)
      refetch()
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al crear el lote')
    },
  })

  const createProductMutation = useMutation({
    mutationFn: (params: {
      name: string
      type: string
      prices: [{ price: number; codeCurrency: string }]
    }) => postProducts(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })

  const updateBatchMutation = useMutation({
    mutationFn: ({ id, params }: { id: number; params: BatchFormData }) => {
      // When editing, productId should not be sent (it's immutable)
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      const { productId, ...updateParams } = params
      return patchContainerBatch({
        idBatch: id,
        params: {
          expirationAt: updateParams.expirationAt as string,
          entryQuantity: Number(updateParams.entryQuantity),
          noPackages: updateParams.noPackages
            ? Number(updateParams.noPackages)
            : undefined,
          supplierId: updateParams.supplierId
            ? Number(updateParams.supplierId)
            : undefined,
          registeredPrice: {
            amount: Number(updateParams.registeredPrice.amount),
            codeCurrency: updateParams.registeredPrice.codeCurrency,
          },
          description: updateParams.description || undefined,
        },
      })
    },
    onSuccess: () => {
      toast.success('Lote actualizado correctamente')
      setShowBatchDialog(false)
      setEditingBatch(null)
      setEditingBatchFull(null)
      setBatchMode('create')
      refetch()
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al actualizar el lote')
    },
  })

  const [showDispatchDialog, setShowDispatchDialog] = useState(false)
  const [selectedStockAreaId, setSelectedStockAreaId] = useState<string>('')
  // const [dispatchObservations, setDispatchObservations] = useState('') // Not used in ungroup endpoint
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'container' | 'batch' | 'document' | 'cost'
    id: number
  } | null>(null)
  const [showCostDialog, setShowCostDialog] = useState(false)
  const [costMode, setCostMode] = useState<'create' | 'edit'>('create')
  const [editingCost, setEditingCost] = useState<Partial<CostFormData> | null>(
    null
  )
  const [editingCostCategory, setEditingCostCategory] = useState<{
    id: number
    name: string
    description?: string
  } | null>(null)
  const [showDocumentDialog, setShowDocumentDialog] = useState(false)
  const [showEditDocumentDialog, setShowEditDocumentDialog] = useState(false)
  const [editingDocument, setEditingDocument] =
    useState<Partial<EditDocumentFormData> | null>(null)
  const [showBatchDialog, setShowBatchDialog] = useState(false)
  const [batchMode, setBatchMode] = useState<'create' | 'edit'>('create')
  const [editingBatch, setEditingBatch] =
    useState<Partial<BatchFormData> | null>(null)
  const [editingBatchFull, setEditingBatchFull] = useState<BatchRow | null>(
    null
  )
  const [showEditDetailsDialog, setShowEditDetailsDialog] = useState(false)
  const [showEditAdvancedDetailsDialog, setShowEditAdvancedDetailsDialog] =
    useState(false)
  const [showAseguramientoDialog, setShowAseguramientoDialog] = useState(false)
  const [isUpdatingDetails, setIsUpdatingDetails] = useState(false)
  const [isUpdatingAdvancedDetails, setIsUpdatingAdvancedDetails] =
    useState(false)

  const [showProductDialog, setShowProductDialog] = useState(false)

  const handleEditDetails = () => {
    setShowEditDetailsDialog(true)
  }

  const handleEditAdvancedDetails = () => {
    setShowEditAdvancedDetailsDialog(true)
  }

  const handleEditInsurance = () => {
    setShowAseguramientoDialog(true)
  }

  const handleSaveDetails = (data: {
    status?: string
    name: string
    operationNumber: string
    countryFromId?: number
    categories?: number[]
    isReturned: boolean
    ownerCompanyId?: number
    managerCompanyId?: number
    observations?: string
  }) => {
    setIsUpdatingDetails(true)
    updateDetailsMutation.mutate(data, {
      onSuccess: () => {
        toast.success('Detalles actualizados correctamente')
        setShowEditDetailsDialog(false)
        setIsUpdatingDetails(false)
        refetch()
      },
      onError: (error: unknown) => {
        const errorMessage = getErrorMessage(error)
        toast.error(errorMessage || 'Error al actualizar los detalles')
        setIsUpdatingDetails(false)
      },
    })
  }

  const handleSaveAdvancedDetails = (data: {
    originId?: number
    destinyId?: number
    netWeight?: number
    grossWeight?: number
    code?: string
    sealNumber?: string
    isRefrigered: boolean
    temperatureRange?: string
    trackingNumber?: string
  }) => {
    setIsUpdatingAdvancedDetails(true)
    updateAdvancedDetailsMutation.mutate(data, {
      onSuccess: () => {
        toast.success('Detalles avanzados actualizados correctamente')
        setShowEditAdvancedDetailsDialog(false)
        setIsUpdatingAdvancedDetails(false)
        refetch()
      },
      onError: (error: unknown) => {
        const errorMessage = getErrorMessage(error)
        toast.error(
          errorMessage || 'Error al actualizar los detalles avanzados'
        )
        setIsUpdatingAdvancedDetails(false)
      },
    })
  }

  const handleAddBatch = () => {
    setBatchMode('create')
    setEditingBatch(null)
    setShowBatchDialog(true)
  }

  const handleEditBatch = (batch: BatchRow) => {
    setBatchMode('edit')
    // Save full batch data for passing to dialog
    setEditingBatchFull(batch)
    // Map to form data - map description from backend to form
    setEditingBatch({
      id: batch.id,
      productId: batch.product?.id || 0,
      expirationAt: batch.expirationAt || '',
      entryQuantity: batch.entryQuantity || 0,
      noPackages: batch.noPackages ?? undefined,
      supplierId: batch.supplier?.id || 0,
      registeredPrice: {
        amount: batch.registeredPrice?.amount,
        codeCurrency: batch.registeredPrice?.codeCurrency || 'USD',
      },
      description:
        (batch as unknown as { description?: string }).description || '',
    })
    setShowBatchDialog(true)
  }

  const handleSaveBatch = (data: BatchFormData) => {
    if (batchMode === 'edit' && data.id) {
      updateBatchMutation.mutate({ id: data.id, params: data })
    } else {
      createBatchMutation.mutate(data)
    }
  }

  const handleCancelBatch = () => {
    setShowBatchDialog(false)
    setEditingBatch(null)
    setEditingBatchFull(null)
    setBatchMode('create')
  }

  const handleAddProduct = () => {
    setShowProductDialog(true)
  }

  const handleAddDocument = () => {
    setShowDocumentDialog(true)
  }

  const handleSaveDocument = (data: {
    name: string
    path: string
    observations?: string
  }) => {
    createDocumentMutation.mutate(data)
  }

  const handleEditDocument = (doc: DocumentRow) => {
    const fullName = doc.name || ''

    // Si el nombre empieza con punto (es solo extensión), mostrar vacío en el nombre
    if (fullName.startsWith('.')) {
      setEditingDocument({
        id: doc.id,
        name: '',
        observations:
          (doc as unknown as { observations?: string }).observations || '',
        originalExtension: fullName,
      })
      setShowEditDocumentDialog(true)
      return
    }

    // Separar nombre y extensión normalmente
    const lastDotIndex = fullName.lastIndexOf('.')
    const hasExtension = lastDotIndex !== -1 && lastDotIndex > 0
    const nameWithoutExtension = hasExtension
      ? fullName.substring(0, lastDotIndex)
      : fullName
    const extension = hasExtension ? fullName.substring(lastDotIndex) : ''

    setEditingDocument({
      id: doc.id,
      name: nameWithoutExtension,
      observations:
        (doc as unknown as { observations?: string }).observations || '',
      originalExtension: extension,
    })
    setShowEditDocumentDialog(true)
  }

  const handleSaveEditDocument = (data: EditDocumentFormData) => {
    if (data.id) {
      const extension = editingDocument?.originalExtension || ''
      const newName = data.name?.trim() || ''
      const fullName = newName + extension

      updateDocumentMutation.mutate({
        id: data.id,
        params: {
          name: fullName,
          observations: data.observations,
        },
      })
    }
  }

  const handleAddCost = () => {
    setCostMode('create')
    setEditingCost(null)
    setEditingCostCategory(null)
    setShowCostDialog(true)
  }

  const handleEditCost = (cost: CostRow) => {
    setCostMode('edit')
    setEditingCost({
      id: cost.id,
      category: cost.fixedCostCategory?.id || 1,
      cost: {
        amount:
          cost.registeredPrice?.amount ??
          cost.price?.amount ??
          (cost.costAmount as number) ??
          cost.cost?.amount ??
          0,
        codeCurrency:
          cost.registeredPrice?.codeCurrency ??
          cost.price?.codeCurrency ??
          cost.costCodecurrency ??
          cost.cost?.codeCurrency ??
          'USD',
      },
      type: cost.type || 'SUPPLIER',
      observations:
        (cost as unknown as { observations?: string }).observations || '',
    })
    setEditingCostCategory(cost.fixedCostCategory || null)
    setShowCostDialog(true)
  }

  const handleSaveCost = (data: CostFormData) => {
    const params: {
      type: string
      registeredPrice: { amount: number; codeCurrency: string }
      fixedCostCategoryId: number
      observations?: string
    } = {
      type: data.type,
      registeredPrice: {
        amount: Number(data.cost.amount),
        codeCurrency: data.cost.codeCurrency,
      },
      fixedCostCategoryId: Number(data.category),
    }

    if (data.observations && data.observations.trim() !== '') {
      params.observations = data.observations
    }

    if (costMode === 'edit' && data.id) {
      updateCostMutation.mutate({
        id: data.id,
        params,
      })
    } else {
      createCostMutation.mutate(params)
    }
  }

  const handleSaveProduct = async (data: ProductFormData) => {
    try {
      // Validate required fields
      if (!data.salePrice.amount || !data.registeredPrice.amount) {
        toast.error('Los precios son requeridos')
        return
      }

      const productResponse = await createProductMutation.mutateAsync({
        name: data.name,
        type: data.type,
        prices: [
          {
            price: Number(data.salePrice.amount),
            codeCurrency: data.salePrice.codeCurrency,
          },
        ],
      })
      const productId = Number(productResponse.data.id)
      await createBatchMutation.mutateAsync({
        productId,
        expirationAt: data.expirationAt,
        entryQuantity: Number(data.entryQuantity),
        noPackages: data.noPackages ? Number(data.noPackages) : undefined,
        supplierId: Number(data.supplierId),
        registeredPrice: {
          amount: Number(data.registeredPrice.amount),
          codeCurrency: data.registeredPrice.codeCurrency,
        },
      })
      setShowProductDialog(false)
      toast.success('Producto y lote creados correctamente')
      refetch()
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al crear el producto o lote')
    }
  }

  const handleDispatch = () => {
    setShowDispatchDialog(true)
  }

  const handleConfirmDispatch = () => {
    if (!selectedStockAreaId) return

    dispatchContainerMutation.mutate(
      {
        containerId: containerId || '',
        areaId: Number(selectedStockAreaId),
        // observations: dispatchObservations || undefined, // Not used in ungroup endpoint
      },
      {
        onSuccess: () => {
          setShowDispatchDialog(false)
          setSelectedStockAreaId('')
          // setDispatchObservations('') // Not used in ungroup endpoint
          refetch()
        },
      }
    )
  }

  const handleCancelDispatch = () => {
    setShowDispatchDialog(false)
    setSelectedStockAreaId('')
    // setDispatchObservations('') // Not used in ungroup endpoint
  }
  /* 
  const handlePrint = () => {
    toast.info('Imprimir - próximamente')
  }

  const handleDuplicate = () => {
    toast.info('Duplicar - próximamente')
  }

  const handleExport = () => {
    toast.info('Exportar - próximamente')
  }

  const handleDelete = () => {
    setDeleteTarget({ type: 'container', id: containerIdNum })
    setShowDeleteDialog(true)
  } */

  const handleDeleteBatch = (batchId: number) => {
    setDeleteTarget({ type: 'batch', id: batchId })
    setShowDeleteDialog(true)
  }

  const handleDeleteDocument = (docId: number) => {
    setDeleteTarget({ type: 'document', id: docId })
    setShowDeleteDialog(true)
  }

  const handleDeleteCost = (costId: number) => {
    setDeleteTarget({ type: 'cost', id: costId })
    setShowDeleteDialog(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return

    setIsDeleting(true)
    try {
      if (deleteTarget.type === 'container') {
        await deleteContainerMutation.mutateAsync()
        toast.success('Contenedor eliminado correctamente')
        navigate('/logistic/containers')
      } else if (deleteTarget.type === 'batch') {
        await deleteContainerBatch(deleteTarget.id)
        toast.success('Lote eliminado correctamente')
        refetch()
      } else if (deleteTarget.type === 'document') {
        await deleteContainerDocuments(deleteTarget.id)
        toast.success('Documento eliminado correctamente')
        refetch()
      } else if (deleteTarget.type === 'cost') {
        await deleteContainerCosts(deleteTarget.id)
        toast.success('Costo eliminado correctamente')
        refetch()
      }
      setShowDeleteDialog(false)
      setDeleteTarget(null)
    } catch {
      const typeLabel =
        deleteTarget.type === 'container'
          ? 'contenedor'
          : deleteTarget.type === 'batch'
            ? 'lote'
            : deleteTarget.type === 'document'
              ? 'documento'
              : 'costo'
      toast.error(`Error al eliminar ${typeLabel}`)
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return <Loading loading />
  }

  if (!data?.details) {
    return (
      <PageWrapper>
        <PageWrapper.Header
          breadcrumbIcon={<ClipboardCheckIcon className="h-4 text-gray-400" />}
          breadcrumbItems={[
            { label: 'Logística' },
            {
              label: 'Contenedores',
              onClick: () => navigate('/logistic/containers'),
            },
            { label: 'No encontrado' },
          ]}
        />
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">Contenedor no encontrado</p>
        </div>
      </PageWrapper>
    )
  }

  const { details, costs, documents, batches, logistics } = data

  const batchColumns: TableColumn<BatchRow>[] = [
    {
      key: 'name',
      label: 'Nombre',
      render: (batch) => batch.product?.name || batch.name || '-',
    },
    {
      key: 'entryQuantity',
      label: 'Cantidad Inicial',
      render: (batch) => String(batch.entryQuantity || batch.quantity || 0),
    },
    {
      key: 'availableQuantity',
      label: 'Cantidad Disponible',
      render: (batch) => String(batch.availableQuantity || 0),
    },
    {
      key: 'grossCost',
      label: 'Costo Bruto',
      render: (batch) =>
        batch.grossCost?.amount
          ? `${Number(batch.grossCost.amount).toFixed(2)} ${
              batch.grossCost.codeCurrency || ''
            }`
          : '-',
    },
    {
      key: 'netCost',
      label: 'Costo neto',
      render: (batch) =>
        batch.netCost?.amount
          ? `${Number(batch.netCost.amount).toFixed(2)} ${
              batch.netCost.codeCurrency || ''
            }`
          : '-',
    },
  ]

  const documentColumns: TableColumn<DocumentRow>[] = [
    {
      key: 'name',
      label: 'Nombre',
      render: (doc) => {
        if (!doc.name) return '-'
        const trimmedName = doc.name.trim()
        if (trimmedName.startsWith('.')) return '-'
        return trimmedName
      },
    },
    {
      key: 'observations',
      label: 'Observaciones',
      render: (doc) => doc.observations || '-',
    },
  ]

  const costColumns: TableColumn<CostRow>[] = [
    {
      key: 'type',
      label: 'Tipo',
      render: (cost) => translateCostType(cost.type || ''),
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (cost) => {
        const rawAmount =
          cost.registeredPrice?.amount ??
          cost.price?.amount ??
          cost.costAmount ??
          cost.cost?.amount ??
          undefined

        if (typeof rawAmount === 'number' && !Number.isNaN(rawAmount)) {
          // if int add .00
          if (Number.isInteger(rawAmount)) {
            return rawAmount.toFixed(2)
          }
          return rawAmount.toString()
        }
        const parsed = Number(rawAmount as unknown as number)
        if (!Number.isNaN(parsed)) {
          if (Number.isInteger(parsed)) {
            return parsed.toFixed(2)
          }
          return parsed.toString()
        }
        return '-'
      },
    },
    {
      key: 'currency',
      label: 'Moneda',
      render: (cost) =>
        cost.registeredPrice?.codeCurrency ??
        cost.price?.codeCurrency ??
        cost.costCodecurrency ??
        cost.cost?.codeCurrency ??
        cost.costCurrency ??
        '-',
    },
  ]

  return (
    <PageWrapper>
      <PageWrapper.Header
        actionButtons={[
          {
            label: 'Desagrupar',
            icon: <ArrowRightToLine className="h-4 w-4" />,
            onClick: handleDispatch,
            variant: 'primary',
            disabled: details.isDispatched,
          },
          /* {
            label: 'Duplicar',
            icon: <DocumentDuplicateIcon className="h-4 w-4" />,
            onClick: handleDuplicate,
            variant: 'secondary',
          },
          {
            label: 'Imprimir',
            icon: <PrinterIcon className="h-4 w-4" />,
            onClick: handlePrint,
            variant: 'secondary',
          },
          {
            label: 'Exportar',
            icon: <ArrowDownTrayIcon className="h-4 w-4" />,
            onClick: handleExport,
            variant: 'secondary',
          },
          {
            label: 'Eliminar',
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleDelete,
            variant: 'danger',
          }, */
        ]}
        breadcrumbIcon={
          <ClipboardCheckIcon className="h-4 w-4 text-gray-400" />
        }
        breadcrumbItems={[
          { label: 'Logística' },
          {
            label: 'Contenedores',
            onClick: () => navigate('/logistic/containers'),
          },
          { label: details.name || `#${details.operationNumber}` },
        ]}
        titleSection={{
          title: `Contenedor: ${details.name}`,
          badge: (
            <StatusBadgeWithIcon
              status={details.status}
              statusType="container"
            />
          ),
          subtitle: `Creado el ${formatDateSimple(details.createdAt)}`,
          metadata: (
            <span>
              <span>Número de operación:</span> {details.operationNumber}
            </span>
          ),
        }}
      />

      <DetailPageLayout.Content>
        <DetailPageLayout.Sidebar>
          {/* Detalles */}
          <EditableCard onEdit={handleEditDetails} title="Detalles">
            <DetailRow
              label="País de origen"
              value={details.countryFrom?.name}
            />
            <DetailRow
              label="Categorías"
              value={
                details.categories?.length > 0
                  ? details.categories.map((c) => c.name).join(', ')
                  : undefined
              }
            />
            <DetailRow
              label="Retornado"
              value={details.isReturned ? 'Sí' : 'No'}
            />
            <DetailRow
              label="Compañía proveedora"
              value={details.ownerCompany?.name}
            />
            <DetailRow
              label="Operador logístico"
              value={details.managerCompany?.name}
            />
            <DetailRow label="Observaciones" value={details.observations} />
          </EditableCard>

          {/* Detalles Avanzados */}
          <EditableCard
            onEdit={handleEditAdvancedDetails}
            title="Detalles Avanzados"
          >
            <DetailRow label="Origen" value={details.origin?.name} />
            <DetailRow label="Destino" value={details.destiny?.name} />
            <DetailRow
              label="Peso neto"
              value={details.netWeight ? `${details.netWeight} kg` : undefined}
            />
            <DetailRow
              label="Peso bruto"
              value={
                details.grossWeight ? `${details.grossWeight} kg` : undefined
              }
            />
            <DetailRow label="Código" value={details.code} />
            <DetailRow label="Sello" value={details.sealNumber} />
            <DetailRow
              label="Refrigeración"
              value={details.isRefrigered ? 'Sí' : 'No'}
            />
            <DetailRow
              label="Rango de temperatura"
              value={details.temperatureRange}
            />
            <DetailRow
              label="Número de tracking"
              value={details.trackingNumber}
            />
          </EditableCard>

          <EditableCard onEdit={handleEditInsurance} title="Aseguramiento">
            <DetailRow
              label="Vehículo"
              value={logistics?.vehicle?.name || details.vehicle?.name}
            />
            <DetailRow
              label="Transportado por"
              value={
                logistics?.transportedBy?.displayName ||
                details.transportedBy?.displayName
              }
            />
            <DetailRow
              label="Transportista"
              value={
                logistics?.transporterCompany?.name ||
                details.transporterCompany?.name
              }
            />
            <DetailRow
              label="Almacenaje"
              value={
                logistics?.storageCompany?.name || details.storageCompany?.name
              }
            />
          </EditableCard>
        </DetailPageLayout.Sidebar>

        <DetailPageLayout.Main alignWithSecondCard>
          {/* Productos/Lote */}
          <ContentCard minHeight="25rem">
            <ContentCard.Header
              actions={
                <>
                  <ActionButton
                    icon={<PlusIcon />}
                    onClick={handleAddProduct}
                    variant="primary"
                  >
                    Producto
                  </ActionButton>
                  <ActionButton
                    icon={<PlusIcon />}
                    onClick={handleAddBatch}
                    variant="primary"
                  >
                    Lote
                  </ActionButton>
                </>
              }
              title="Productos/Lote"
            />
            <ContentCard.Body scrollHeight="17.3rem">
              <GenericTable<BatchRow>
                actions={[
                  {
                    icon: <TrashIcon className="h-4 w-4" />,
                    label: 'Eliminar',
                    onClick: (batch) => handleDeleteBatch(batch.id || 0),
                    variant: 'danger',
                  },
                ]}
                className="cursor-pointer"
                columns={batchColumns}
                data={batches || []}
                emptyMessage="No existen resultados"
                getRowKey={(batch) => batch.id || 0}
                onEdit={handleEditBatch}
                showDefaultActions={false}
              />
            </ContentCard.Body>
          </ContentCard>

          {/* Documentos */}
          <ContentCard minHeight="25rem">
            <ContentCard.Header
              actions={
                <ActionButton
                  icon={<PaperClipIcon />}
                  onClick={handleAddDocument}
                  variant="primary"
                >
                  Adjuntar Documentos
                </ActionButton>
              }
              title="Documentos"
            />
            <ContentCard.Body scrollHeight="17.3rem">
              <GenericTable<DocumentRow>
                actions={[
                  {
                    icon: <ArrowDownTrayIcon className="h-4 w-4 text-black" />,
                    label: 'Descargar',
                    onClick: (doc) => {
                      if (doc.path) {
                        postMediaUrl({
                          key: doc.path,
                          isPrivate: 'true',
                        }).then((url: string | undefined) => {
                          if (url) {
                            window.open(url, '_blank')
                          }
                        })
                      }
                    },
                    show: (doc) => !!(doc.path && doc.path.trim() !== ''),
                  },
                  {
                    icon: <TrashIcon className="h-4 w-4" />,
                    label: 'Eliminar',
                    onClick: (doc) => handleDeleteDocument(doc.id || 0),
                    variant: 'danger',
                  },
                ]}
                className="cursor-pointer"
                columns={documentColumns}
                data={documents || []}
                emptyMessage="No existen resultados"
                getRowKey={(doc) => doc.id || 0}
                onEdit={handleEditDocument}
                showDefaultActions={false}
              />
            </ContentCard.Body>
          </ContentCard>

          {/* Costos */}
          <ContentCard minHeight="25rem">
            <ContentCard.Header
              actions={
                <ActionButton
                  icon={<PlusIcon />}
                  onClick={handleAddCost}
                  variant="primary"
                >
                  Costo
                </ActionButton>
              }
              title="Costos"
            />
            <ContentCard.Body scrollHeight="17.3rem">
              <GenericTable<CostRow>
                actions={[
                  {
                    icon: <TrashIcon className="h-4 w-4" />,
                    label: 'Eliminar',
                    onClick: (cost) => handleDeleteCost(cost.id || 0),
                    variant: 'danger',
                  },
                ]}
                className="cursor-pointer"
                columns={costColumns}
                data={costs || []}
                emptyMessage="No existen resultados"
                getRowKey={(cost) => cost.id || 0}
                onEdit={(cost) => {
                  handleEditCost(cost)
                }}
                showDefaultActions={false}
              />
            </ContentCard.Body>
          </ContentCard>
        </DetailPageLayout.Main>
      </DetailPageLayout.Content>

      {/* Dispatch Dialog */}
      <DispatchDialog
        containerName={details.name}
        isPending={dispatchContainerMutation.isPending}
        // observations="" // Not used in ungroup endpoint
        onCancel={handleCancelDispatch}
        onConfirm={handleConfirmDispatch}
        onOpenChange={setShowDispatchDialog}
        open={showDispatchDialog}
        selectedStockAreaId={selectedStockAreaId}
        // setObservations={() => {}} // Not used in ungroup endpoint
        setSelectedStockAreaId={setSelectedStockAreaId}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        confirmText="Eliminar"
        description="Esta acción no se puede deshacer. ¿Está seguro de que desea continuar?"
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onOpenChange={setShowDeleteDialog}
        open={showDeleteDialog}
        title={`¿Eliminar ${
          deleteTarget?.type === 'container'
            ? 'contenedor'
            : deleteTarget?.type === 'batch'
              ? 'lote'
              : deleteTarget?.type === 'document'
                ? 'documento'
                : 'costo'
        }?`}
        variant="destructive"
      />

      {/* Cost Dialog */}
      <CostDialog
        defaultCategory={editingCostCategory || undefined}
        initialData={editingCost || undefined}
        isPending={createCostMutation.isPending || updateCostMutation.isPending}
        mode={costMode}
        onCancel={() => setShowCostDialog(false)}
        onOpenChange={setShowCostDialog}
        onSave={handleSaveCost}
        open={showCostDialog}
        showObservations={false}
      />

      {/* Document Dialog */}
      <DocumentDialog
        isPending={createDocumentMutation.isPending}
        onOpenChange={setShowDocumentDialog}
        onSave={handleSaveDocument}
        open={showDocumentDialog}
      />

      {/* Batch Dialog */}
      <BatchDialog
        defaultProduct={
          editingBatchFull?.product
            ? {
                id: editingBatchFull.product.id || 0,
                name: editingBatchFull.product.name || '',
              }
            : undefined
        }
        defaultSupplier={
          editingBatchFull?.supplier
            ? {
                id: editingBatchFull.supplier.id || 0,
                name: editingBatchFull.supplier.name || '',
              }
            : undefined
        }
        initialData={editingBatch || undefined}
        isPending={
          createBatchMutation.isPending || updateBatchMutation.isPending
        }
        mode={batchMode}
        onCancel={handleCancelBatch}
        onOpenChange={setShowBatchDialog}
        onSave={handleSaveBatch}
        open={showBatchDialog}
      />

      {/* Edit Details Dialog */}
      <EditDetailsDialog
        defaultCategories={details?.categories || []}
        defaultCountry={details?.countryFrom || undefined}
        defaultManagerCompany={details?.managerCompany || undefined}
        defaultOwnerCompany={details?.ownerCompany || undefined}
        defaultStatus={
          details?.status
            ? {
                id: details.status,
                name: CONTAINER_STATUS_LABELS[details.status] || details.status,
              }
            : undefined
        }
        fetchCategories={fetchCategories}
        fetchCompanies={fetchCompanies}
        fetchCountries={fetchCountries}
        fetchManagerCompanies={fetchManagerCompanies}
        initialData={{
          status: details?.status,
          name: details?.name || '',
          operationNumber: details?.operationNumber
            ? String(details.operationNumber)
            : '',
          countryFromId: details?.countryFrom?.id,
          categories: details?.categories?.map((c) => c.id) || [],
          isReturned: details?.isReturned || false,
          ownerCompanyId: details?.ownerCompany?.id,
          managerCompanyId: details?.managerCompany?.id,
          observations: details?.observations,
        }}
        isPending={isUpdatingDetails}
        onCancel={() => setShowEditDetailsDialog(false)}
        onOpenChange={setShowEditDetailsDialog}
        onSave={handleSaveDetails}
        open={showEditDetailsDialog}
      />

      {/* Edit Advanced Details Dialog */}
      <EditAdvancedDetailsDialog
        defaultDestination={details?.destiny || undefined}
        defaultOrigin={details?.origin || undefined}
        fetchDestinations={fetchDestinations}
        fetchOrigins={fetchOrigins}
        initialData={{
          originId: details?.origin?.id,
          destinyId: details?.destiny?.id,
          netWeight: details?.netWeight,
          grossWeight: details?.grossWeight,
          code: details?.code,
          sealNumber: details?.sealNumber,
          isRefrigered: details?.isRefrigered || false,
          temperatureRange: details?.temperatureRange,
          trackingNumber: details?.trackingNumber
            ? String(details.trackingNumber)
            : undefined,
        }}
        isPending={isUpdatingAdvancedDetails}
        onCancel={() => setShowEditAdvancedDetailsDialog(false)}
        onOpenChange={setShowEditAdvancedDetailsDialog}
        onSave={handleSaveAdvancedDetails}
        open={showEditAdvancedDetailsDialog}
      />

      {/* Edit Logistics Dialog */}
      <InsuranceDialog
        containerId={containerIdNum}
        isOpen={showAseguramientoDialog}
        onClose={() => setShowAseguramientoDialog(false)}
        onSuccess={() => {
          refetch()
        }}
      />

      {/* Product Dialog */}
      <ProductDialog
        isPending={
          createProductMutation.isPending || createBatchMutation.isPending
        }
        onCancel={() => setShowProductDialog(false)}
        onOpenChange={setShowProductDialog}
        onSave={handleSaveProduct}
        open={showProductDialog}
      />

      {/* Edit Document Dialog */}
      <EditDocumentDialog
        initialData={editingDocument || undefined}
        isPending={updateDocumentMutation.isPending}
        onOpenChange={setShowEditDocumentDialog}
        onSave={handleSaveEditDocument}
        open={showEditDocumentDialog}
      />
    </PageWrapper>
  )
}
